import { apiGet } from './client';

// Workbench MVP Session C — Model Routing Simulator.
// Given a request class + optional cost/latency budgets, rank the models the
// platform has actually measured (from MatrixResults) and pick a winner. Read
// only — no provider is called and nothing is persisted.

export type RoutingRequestClass =
  | 'cost-optimized'
  | 'balanced'
  | 'quality-critical'
  | 'latency-critical';

export type RoutingStatus = 'ok' | 'no_feasible_candidate' | 'no_data';

export interface RoutingWeights {
  quality: number;
  cost: number;
  latency: number;
}

export interface RoutingCandidate {
  chatModelId: string;
  providerId: string;
  providerName: string;
  modelName: string;
  displayLabel: string;
  tierHint: string | null;
  isConfigured: boolean;
  sampleCount: number;
  avgCostUsd: number;
  avgLatencyMs: number;
  p95LatencyMs: number;
  avgQualityScore: number;
  eligible: boolean;
  ineligibleReason: string | null;
  score: number | null;
  rank: number | null;
  isChosen: boolean;
}

export interface RoutingSimulation {
  requestClass: RoutingRequestClass;
  weights: RoutingWeights;
  windowDays: number;
  windowStart: string;
  windowEnd: string;
  minSamples: number;
  maxCostUsd: number | null;
  maxLatencyMs: number | null;
  status: RoutingStatus;
  chosenModelId: string | null;
  rationale: string;
  candidates: RoutingCandidate[];
  generatedAt: string;
}

// Static class metadata for the picker. The backend owns the actual weights —
// these labels/blurbs are display-only, and the resolved weights come back in
// the response so the UI never has to keep them in sync.
export const REQUEST_CLASSES: {
  id: RoutingRequestClass;
  label: string;
  blurb: string;
}[] = [
  { id: 'cost-optimized',   label: 'Cost-optimized',   blurb: 'Cheapest acceptable model — batch / bulk workloads.' },
  { id: 'balanced',         label: 'Balanced',         blurb: 'Even weighting across quality, cost and latency.' },
  { id: 'quality-critical', label: 'Quality-critical', blurb: 'Quality dominates — clinical / high-stakes reasoning.' },
  { id: 'latency-critical', label: 'Latency-critical', blurb: 'Latency dominates — interactive, user-waiting requests.' },
];

export function simulateRouting(params: {
  requestClass: RoutingRequestClass;
  maxCostUsd?: number;
  maxLatencyMs?: number;
  windowDays?: number;
}): Promise<RoutingSimulation> {
  const q = new URLSearchParams({
    requestClass: params.requestClass,
    maxCostUsd: String(params.maxCostUsd ?? 0),
    maxLatencyMs: String(params.maxLatencyMs ?? 0),
    windowDays: String(params.windowDays ?? 7),
  });
  return apiGet<RoutingSimulation>(`/api/routing/simulate?${q.toString()}`);
}
